import { useState, useEffect } from 'react';
import { channelLinkPayload } from '@features/ChannelLocationSelect';
import { useDispatch } from 'react-redux';
import { FormProvider } from 'react-hook-form';
import { channelsActions, alertActions } from '@actions';
import { channelsService } from '@services';
import { CHANNEL, HOSTED_EMAIL_FORM, STATUS, SmsProviderType, isHostedEmailChannel } from '@constants/channels';
import { PREVIEW_LOCALES } from '@constants/microsites';
import LiveChatCode from '@features/ChannelsForms/LiveChatHTMLCode';
import LiveChatMessages from '@features/ChannelsForms/LiveChatMessages';
import FacebookForm from '@features/ChannelsForms/FacebookForm';
import SmsForm from '@features/ChannelsForms/SmsForm';
import EmailForm from '@features/ChannelsForms/EmailForm';
import HostedEmailForm from '@features/ChannelsForms/HostedEmailForm';
import WhatsAppForm from '@features/ChannelsForms/WhatsAppForm';
import LiveChatForm from '@features/ChannelsForms/LiveChatForm';
import {
    isDefaultLiveChatTextComplete,
    toLiveChatFormValues,
    toLiveChatPayload,
    validateLiveChatValues,
} from '@features/ChannelsForms/LiveChatForm/payload';
import WhatsAppBusinessForm from '@features/ChannelsForms/WhatsAppBusinessForm';
import { getBrowserTimeZone } from '@helpers/dates';
import { handleApiError } from '@helpers/api';
import { smsProviderPayload } from '@features/ChannelsForms/SmsForm/payload';
import { setInitialPhoneNumberToForm } from '@helpers/phone';
import { useTranslation, useEditChannelsForm, useLocations } from '@hooks';
import { Modal, TabsWrapper, StyledRadioButtons, TabErrorDot } from './styles';

const Tab = {
    Settings: 0,
    Messages: 1,
    Code: 2,
};

const EditModal = ({
    open,
    setOpen,
    selectedChannel,
    setSelectedChannel,
    onClickDelete,
    accountLocale,
    companies = [],
}) => {
    const { t } = useTranslation();

    const [tab, setTab] = useState(Tab.Settings);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailDomain, setEmailDomain] = useState(null);
    const isHostedEmail = isHostedEmailChannel(selectedChannel);

    const dispatch = useDispatch();
    const { mapSelectedLocationsToFormValue } = useLocations();
    const { getValues, setValue, control, reset, watch, trigger, isValid, isDirty } = useEditChannelsForm(
        isHostedEmail ? HOSTED_EMAIL_FORM : selectedChannel?.type,
    );
    const canSaveUnchanged = selectedChannel?.type === CHANNEL.SMS && selectedChannel?.status === STATUS.DISCONNECTED;

    useEffect(() => {
        setEmailDomain(null);
        if (!isHostedEmail || !selectedChannel?.emailDomainId) return undefined;
        let cancelled = false;
        channelsService
            .getEmailDomain(selectedChannel.emailDomainId)
            .then(domain => {
                if (!cancelled) setEmailDomain(domain);
            })
            .catch(error => {
                if (!cancelled) handleApiError(error);
            });
        return () => {
            cancelled = true;
        };
    }, [isHostedEmail, selectedChannel?.emailDomainId]);

    useEffect(() => {
        if (selectedChannel?.type === CHANNEL.SMS) {
            setValue('name', selectedChannel?.name || '', { shouldValidate: true });
            setValue(
                'phoneNumber',
                setInitialPhoneNumberToForm(selectedChannel?.smsParameters?.phoneNumber, accountLocale),
                { shouldValidate: true },
            );
            setValue('senderId', selectedChannel?.smsParameters?.senderId || '', { shouldValidate: true });
            setValue('provider', selectedChannel?.smsParameters?.provider || 0, { shouldValidate: true });
            setValue('integrationId', selectedChannel?.integrationId || '', { shouldValidate: true });
            if (selectedChannel?.smsParameters?.provider === SmsProviderType.RingCentral) {
                setValue('phoneNumber', selectedChannel?.smsParameters?.phoneNumber || '', { shouldValidate: true });
            }
            setValue('accountId', selectedChannel?.smsParameters?.accountId || '', {
                shouldValidate: true,
            });
            setValue('apiKey', selectedChannel?.smsParameters?.apiKey || '', { shouldValidate: true });
            setValue('token', selectedChannel?.smsParameters?.token || '', { shouldValidate: true });
            setValue('login', selectedChannel?.smsParameters?.login || '', { shouldValidate: true });
            setValue('password', selectedChannel?.smsParameters?.password || '', { shouldValidate: true });
            return;
        }
        if (selectedChannel?.type === CHANNEL.WHATSAPP) {
            setValue('name', selectedChannel?.name || '', { shouldValidate: true });
            setValue('phoneNumber', selectedChannel?.whatsAppParameters?.phoneNumber || '', { shouldValidate: true });
            return;
        }
        if (selectedChannel?.type === CHANNEL.WHATSAPP_BUSINESS) {
            setValue('name', selectedChannel?.name || '', { shouldValidate: true });
            setValue('phoneNumber', selectedChannel?.whatsAppParameters?.phoneNumber || '', { shouldValidate: true });
            setValue('phoneNumberId', selectedChannel?.whatsAppParameters?.phoneNumberId || '', {
                shouldValidate: true,
            });
            setValue('phoneNumberName', selectedChannel?.whatsAppParameters?.phoneNumberName || '', {
                shouldValidate: true,
            });
            setValue('whatsAppBusinessId', selectedChannel?.whatsAppParameters?.whatsAppBusinessId || '', {
                shouldValidate: true,
            });
            return;
        }
        if (selectedChannel?.type === CHANNEL.LIVE_CHAT) {
            const values = toLiveChatFormValues(selectedChannel, {
                currentLocale: PREVIEW_LOCALES[accountLocale],
                timeZone: getBrowserTimeZone(),
                locations: companies,
                mapSelectedLocationsToFormValue,
            });
            Object.entries(values).forEach(([name, value]) => setValue(name, value, { shouldValidate: true }));
            return;
        }
        if (selectedChannel?.type === CHANNEL.FACEBOOK || selectedChannel?.type === CHANNEL.INSTAGRAM) {
            setValue('name', selectedChannel?.name || '', { shouldValidate: true });
            setValue('id', selectedChannel?.id || '', { shouldValidate: true });
            selectedChannel?.type === CHANNEL.INSTAGRAM &&
                setValue('saveInstagramReplies', selectedChannel?.facebookParameters?.saveInstagramReplies, {
                    shouldValidate: true,
                });
            return;
        }
        if (isHostedEmailChannel(selectedChannel)) {
            setValue('name', selectedChannel?.name || '', { shouldValidate: true });
            setValue('email', selectedChannel?.emailParameters?.email || '', { shouldValidate: true });
            setValue('enableIncomingEmail', !!selectedChannel?.incoming, { shouldValidate: true });
            return;
        }
        if (selectedChannel?.type === CHANNEL.EMAIL) {
            setValue('name', selectedChannel?.name || '', { shouldValidate: true });
            setValue('provider', selectedChannel?.emailParameters?.provider || 0, {
                shouldValidate: true,
            });
            setValue('smtpHost', selectedChannel?.emailParameters?.smtpHost || '', {
                shouldValidate: true,
            });
            setValue('smtpPort', selectedChannel?.emailParameters?.smtpPort || '', {
                shouldValidate: true,
            });
            setValue('username', selectedChannel?.emailParameters?.username || '', {
                shouldValidate: true,
            });
            setValue('isSSL', selectedChannel?.emailParameters?.isSSL, { shouldValidate: true });
            setValue('email', selectedChannel?.emailParameters?.email || '', { shouldValidate: true });
            setValue('password', selectedChannel?.emailParameters?.password || '', {
                shouldValidate: true,
            });
            setValue('enableIncomingEmail', selectedChannel?.emailParameters?.enableIncomingEmail || false, {
                shouldValidate: true,
            });
            setValue('imapHost', selectedChannel?.emailParameters?.imapHost || '', {
                shouldValidate: true,
            });
            setValue('imapPort', selectedChannel?.emailParameters?.imapPort || '', {
                shouldValidate: true,
            });
            setValue('imapUsername', selectedChannel?.emailParameters?.imapUsername || '');
            setValue('imapPassword', selectedChannel?.emailParameters?.imapPassword || '');
            setValue('signature', selectedChannel?.emailParameters?.signature || '');
            return;
        }
    }, [selectedChannel, setValue, accountLocale]);

    const onClickSubmit = async () => {
        setIsSubmitting(true);
        const values = getValues();

        if (selectedChannel?.type === CHANNEL.LIVE_CHAT) {
            const errorCode = validateLiveChatValues(values);
            if (errorCode) {
                dispatch(alertActions.error(t(`apiErrors.${errorCode}`)));
                setIsSubmitting(false);
                return;
            }
        }
        try {
            if (selectedChannel?.type === CHANNEL.SMS) {
                await channelsService.createOrUpdateChannelSms({
                    id: selectedChannel?.id,
                    name: values.name,
                    teamId: values.teamId,
                    ...channelLinkPayload(values),
                    ...smsProviderPayload(values),
                });
            } else if (selectedChannel?.type === CHANNEL.WHATSAPP) {
                await channelsService.createOrUpdateChannelWhatsApp({
                    id: selectedChannel?.id,
                    name: values.name,
                    newWhatsAppAccount: selectedChannel?.newWhatsAppAccount,
                    teamId: values.teamId,
                    ...channelLinkPayload(values),
                });
            } else if (selectedChannel?.type === CHANNEL.WHATSAPP_BUSINESS) {
                await channelsService.createOrUpdateChannelWhatsAppBusiness({
                    id: selectedChannel?.id,
                    name: values.name,
                    teamId: values.teamId,
                    ...channelLinkPayload(values),
                    phoneNumber: values.phoneNumber,
                    phoneNumberId: values.phoneNumberId,
                    phoneNumberName: values.phoneNumberName,
                    whatsAppBusinessId: values.whatsAppBusinessId,
                });
            } else if (selectedChannel?.type === CHANNEL.LIVE_CHAT) {
                await channelsService.createOrUpdateChannelLiveChat(
                    toLiveChatPayload(values, { id: selectedChannel?.id, timeZone: getBrowserTimeZone() }),
                );
            } else if (selectedChannel?.type === CHANNEL.FACEBOOK) {
                await channelsService.createOrUpdateChannelFacebook({
                    id: selectedChannel?.id,
                    ...values,
                    ...channelLinkPayload(values),
                });
            } else if (selectedChannel?.type === CHANNEL.INSTAGRAM) {
                await channelsService.createOrUpdateChannelInstagram({
                    id: selectedChannel?.id,
                    ...values,
                    ...channelLinkPayload(values),
                });
            } else if (isHostedEmail) {
                await channelsService.createOrUpdateChannelEmail({
                    id: selectedChannel?.id,
                    name: values.name,
                    teamId: values.teamId,
                    ...channelLinkPayload(values),
                    emailParameters: {
                        ...selectedChannel.emailParameters,
                        fromName: values.name,
                        enableIncomingEmail: !!values.enableIncomingEmail,
                    },
                });
            } else if (selectedChannel?.type === CHANNEL.EMAIL) {
                await channelsService.createOrUpdateChannelEmail({
                    id: selectedChannel?.id,
                    name: values.name,
                    teamId: values.teamId,
                    ...channelLinkPayload(values),
                    emailParameters: {
                        provider: values.provider,
                        smtpHost: values.smtpHost,
                        smtpPort: values.smtpPort,
                        username: values.username,
                        isSSL: values.isSSL,
                        email: values.email,
                        password: values.password,
                        enableIncomingEmail: values.enableIncomingEmail || false,
                        imapHost: values.imapHost || null,
                        imapPort: values.imapPort || null,
                        imapUsername: values.imapUsername || null,
                        imapPassword: values.imapPassword || null,
                        signature: values.signature || null,
                    },
                });
            }
            await dispatch(channelsActions.get());
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
            onClose();
        } catch (error) {
            handleApiError(error, 'alertMessages.updateFail', { amount: error.count });
        } finally {
            setIsSubmitting(false);
        }
    };

    const onClose = () => {
        setOpen(false);
        setSelectedChannel({});
        reset();
    };

    const getTitleText = () => {
        switch (selectedChannel?.type) {
            case CHANNEL.SMS:
                return t('common.sms');
            case CHANNEL.WHATSAPP:
            case CHANNEL.WHATSAPP_BUSINESS:
                return t('common.whatsApp');
            case CHANNEL.LIVE_CHAT:
                return t('common.liveChatLowercase');
            case CHANNEL.FACEBOOK:
                return t('common.facebookMessenger');
            case CHANNEL.INSTAGRAM:
                return t('common.instagram');
            case CHANNEL.EMAIL:
                return t('common.email');
            default:
                return '';
        }
    };

    const onDelete = () => onClickDelete(selectedChannel);

    return (
        <Modal
            fullScreen
            isOpen={open}
            handleClose={onClose}
            isDirty={isDirty}
            title={t('SettingsChannels.editChannelTitle', { channelType: getTitleText() })}
            subtitle={
                isHostedEmail
                    ? t('SettingsChannels.hostedEmailSubtitle')
                    : t('SettingsChannels.editChannelSubtitle', { channelType: getTitleText() })
            }
            onPrimaryAction={onClickSubmit}
            primaryActionText={t('buttons.saveChanges')}
            primaryActionDisabled={isSubmitting || !isValid || (!isDirty && !canSaveUnchanged)}
            onAdditionalAction={onDelete}
            additionalTextButton={t('buttons.delete')}
        >
            {selectedChannel?.type === CHANNEL.LIVE_CHAT ? (
                <TabsWrapper>
                    <StyledRadioButtons
                        value={tab}
                        onChange={setTab}
                        options={[
                            { value: Tab.Settings, label: t('common.settings') },
                            {
                                value: Tab.Messages,
                                label: isDefaultLiveChatTextComplete(watch('texts'), {
                                    workingHoursEnabled: watch('workingHoursEnabled'),
                                    buttonCount: watch('buttonCount'),
                                }) ? (
                                    t('SettingsChannels.messagesTab')
                                ) : (
                                    <span>
                                        {t('SettingsChannels.messagesTab')}
                                        <TabErrorDot role="img" aria-label={t('validation.required')} />
                                    </span>
                                ),
                            },
                            { value: Tab.Code, label: t('ShowcaseWidgets.widgetCode') },
                        ]}
                    />
                </TabsWrapper>
            ) : null}
            <FormProvider {...{ control, setValue, getValues, watch, trigger }}>
                {selectedChannel?.type === CHANNEL.SMS ? (
                    <SmsForm isEdit twillioUid={selectedChannel?.id} selectedChannel={selectedChannel} />
                ) : null}
                {selectedChannel?.type === CHANNEL.WHATSAPP ? (
                    <WhatsAppForm isEdit selectedChannel={selectedChannel} />
                ) : null}
                {selectedChannel?.type === CHANNEL.WHATSAPP_BUSINESS ? (
                    <WhatsAppBusinessForm isEdit selectedChannel={selectedChannel} />
                ) : null}
                {selectedChannel?.type === CHANNEL.LIVE_CHAT && tab === Tab.Settings ? (
                    <LiveChatForm selectedChannel={selectedChannel} />
                ) : null}
                {selectedChannel?.type === CHANNEL.LIVE_CHAT && tab === Tab.Messages ? <LiveChatMessages /> : null}
                {selectedChannel?.type === CHANNEL.LIVE_CHAT && tab === Tab.Code ? (
                    <LiveChatCode liveChatPublicId={selectedChannel?.publicId} liveChatId={selectedChannel?.id} />
                ) : null}
                {selectedChannel?.type === CHANNEL.FACEBOOK || selectedChannel?.type === CHANNEL.INSTAGRAM ? (
                    <FacebookForm type={selectedChannel?.type} isEdit selectedChannel={selectedChannel} />
                ) : null}
                {isHostedEmail ? (
                    <HostedEmailForm
                        isEdit
                        domain={emailDomain}
                        onDomainUpdated={setEmailDomain}
                        selectedChannel={selectedChannel}
                    />
                ) : null}
                {selectedChannel?.type === CHANNEL.EMAIL && !isHostedEmail ? (
                    <EmailForm selectedChannel={selectedChannel} />
                ) : null}
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
